import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  EMAIL_ALREADY_EXISTS_ERR,
  signUpValidationSchema,
} from "../../../constants/auth";
import { register } from "../../../services/apiV2";
import {
  EMAIL_ALREADY_EXISTS,
  RECAPTCHA_REQUIRED,
  REGISTRATION_FAILED,
} from "../../../constants";
import { axiosErrorMatches } from "../../../helpers";
import ReCAPTCHA from "react-google-recaptcha";
import { useSearchParams } from "react-router-dom";

type TFormFields = {
  email: string;
  password: string;
  fullName: string;
};

export type TErrors = {
  emailAlreadyExists: string | null;
  registrationFailed: string | null;
  recaptchaRequired: string | null;
};

type TUseSignUpProps = {
  onSuccess: (email: string) => void;
};

const ERRORS_INITIAL_STATE: TErrors = {
  emailAlreadyExists: null,
  registrationFailed: null,
  recaptchaRequired: null,
};

const getEmailConfirmationURL = (email: string) =>
  `${process.env.REACT_APP_SERVER_URL}/email-verify?email=${email}&token=`;

export const useSignUp = ({ onSuccess }: TUseSignUpProps) => {
  const [errors, setErrors] = useState<TErrors>(ERRORS_INITIAL_STATE);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<TFormFields>({
    defaultValues: {
      email: "",
      password: "",
      fullName: "",
    },
    resolver: zodResolver(signUpValidationSchema),
    reValidateMode: "onSubmit",
  });

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const getCookie = (name: string) => {
    const cookies = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${name}=`));
    return cookies ? cookies.split("=")[1] : undefined;
  };

  const onSubmit = handleSubmit(async ({ email, fullName, password }) => {
    setErrors(ERRORS_INITIAL_STATE);

    if (!recaptchaToken) {
      setErrors((prev) => ({
        ...prev,
        recaptchaRequired: RECAPTCHA_REQUIRED,
      }));
      return;
    }

    try {
      const promoteKitReferral = getCookie("promotekit_referral");
      await register({
        fullName,
        email,
        password,
        // EmailConfirmationRedirectUrl: getEmailConfirmationURL(email),
        promoteKitReferral,
        registeredFrom:
          searchParams.get("from") === "extension" ? "extension" : "website",
      });
      onSuccess(email);
    } catch (e) {
      if (axiosErrorMatches(e, EMAIL_ALREADY_EXISTS_ERR)) {
        setErrors((prev) => ({
          ...prev,
          emailAlreadyExists: EMAIL_ALREADY_EXISTS,
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          registrationFailed: REGISTRATION_FAILED,
        }));
      }
    }
  });

  return {
    errors,
    control,
    isSubmitting,
    recaptchaRef,
    setRecaptchaToken,
    onSubmit,
  };
};
